import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, FileText, Settings as SettingsIcon, LogOut, BarChart3, Users } from 'lucide-react';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/admin');
  };

  const cards = [
    {
      title: 'Gestionar Proyectos',
      description: 'Creá, editá y eliminá proyectos. Subí imágenes y videos.',
      icon: FileText,
      href: '/admin/proyectos',
      color: 'bg-blue-500',
    },
    {
      title: 'Configuración',
      description: 'Personalizá tu sitio: colores, logo, información de contacto y más.',
      icon: SettingsIcon,
      href: '/admin/configuracion',
      color: 'bg-purple-500',
    },
    {
      title: 'Analytics',
      description: 'Visualizá estadísticas de visitas, tiempo de permanencia y más.',
      icon: BarChart3,
      href: '/admin/analytics',
      color: 'bg-amber-500',
    },
    {
      title: 'Gestión de Usuarios',
      description: 'Administrá usuarios, cambiá tu contraseña y actualizá tu perfil.',
      icon: Users,
      href: '/admin/usuarios',
      color: 'bg-green-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container-safe flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
              <LayoutDashboard size={20} />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-slate-900">Panel de Administración</h1>
              <p className="text-xs text-slate-600">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
              data-testid="view-site-btn"
            >
              Ver sitio
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container-safe py-12">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Bienvenido al Panel</h2>
          <p className="mt-1 text-slate-600">Gestía tu sitio web sin tocar código.</p>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {cards.map((card) => (
            <Link
              key={card.href}
              to={card.href}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`admin-card-${card.title.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${card.color} text-white`}>
                <card.icon size={24} />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-slate-900">{card.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{card.description}</p>
              <div className="mt-4 text-sm font-semibold text-slate-900 group-hover:translate-x-1 transition-transform">
                Abrir →
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};
